import { createContext, useContext, useState, useEffect, useCallback } from 'react'

// Import all locale files
import en from './locales/en.json'
import es from './locales/es.json'
import zh from './locales/zh.json'
import vi from './locales/vi.json'
import ko from './locales/ko.json'
import fr from './locales/fr.json'
import ar from './locales/ar.json'
import ru from './locales/ru.json'
import pt from './locales/pt.json'
import tl from './locales/tl.json'
import hi from './locales/hi.json'
import { approvedText } from './pendingEs'
import { setAppLanguage } from '../utils/appLanguage'

// Supported languages configuration
// Currently supporting English and Spanish, with more languages coming soon
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', comingSoon: true },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', comingSoon: true },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', comingSoon: true },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', comingSoon: true },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true, comingSoon: true },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', comingSoon: true },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', comingSoon: true },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', flag: '🇵🇭', comingSoon: true },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', comingSoon: true },
]

/**
 * ★★★ v242.172.0 — THE ENABLED SET, AND WHY IT IS DERIVED HERE RATHER THAN LISTED.
 *
 * Dr. Rami's ruling, 2026-09-08: the pilot ships English and Spanish. Measured the same
 * day: en.json holds 4,144 keys, es.json carries 4,132, and the other nine carry between
 * 886 and 948 — 17 to 20 per cent — so a patient in one of those nine would be reading a
 * mostly-English app with no indication that is what happened.
 *
 * ★★ THE PICKER WAS ALREADY RIGHT AND THE OWNER WAS NOT. `LanguageSelector` renders the
 * nine `comingSoon` rows disabled and returns early in `handleSelect`, so nobody can
 * choose one FROM THAT SCREEN. But the rule lived only on that screen: `setLanguage`
 * accepted any code with a translation file, and the boot read accepted any code sitting
 * in `localStorage`. So a device carrying `orella-language: 'fr'` from any earlier build —
 * or the dev StreamTestPage's raw <select> — booted into French, and every `?lang=`
 * the app then sends asks the server for French questionnaires. A rule stated at one call
 * site is a rule until somebody adds a second call site (1dn(f)).
 *
 * ★ `comingSoon` is the flag the product already had. Deriving from it means the picker
 *   and the guard cannot disagree: one list, two readers.
 */
export const ENABLED_LANGUAGE_CODES = SUPPORTED_LANGUAGES
  .filter((l) => !l.comingSoon)
  .map((l) => l.code)

export const isLanguageEnabled = (code) => ENABLED_LANGUAGE_CODES.includes(code)

// Map language codes to their translation files
// ★ ALL ELEVEN STAY. The files are not deleted, the parity ratchet in
//   scripts/check-i18n-keys.mjs still measures all ten against en.json, and turning a
//   language back on is one `comingSoon` flag. Nothing is thrown away by this ruling.
const translations = {
  en,
  es,
  zh,
  vi,
  ko,
  fr,
  ar,
  ru,
  pt,
  tl,
  hi,
}

// Storage key for persisting language preference
const LANGUAGE_STORAGE_KEY = 'orella-language'

// Default language
const DEFAULT_LANGUAGE = 'en'

// Create the context
const I18nContext = createContext(null)

/**
 * Get nested value from object using dot notation
 * e.g., getValue(obj, 'auth.signIn') returns obj.auth.signIn
 */
function getValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}

/**
 * I18n Provider Component
 * Wraps the app and provides translation functionality
 */
export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Try to get saved language from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
      // ★★ v242.172.0 — `translations[saved]` was the old test, and a translation FILE
      //   existing is not the same fact as a language being OFFERED. A device that stored
      //   one of the nine before they were gated came back to it on every cold start.
      if (saved && isLanguageEnabled(saved)) {
        setAppLanguage(saved) // PROD-814: non-React helpers (dateUtils) read the same language
        return saved
      }
    }
    return DEFAULT_LANGUAGE
  })

  // Save language preference when it changes
  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  }, [language])

  /**
   * Translate function
   * @param {string} key - Translation key in dot notation (e.g., 'auth.signIn')
   * @param {object} params - Optional parameters for interpolation
   * @returns {string} - Translated string or key if not found
   */
  const t = useCallback((key, params = {}) => {
    // Support inline English fallbacks used throughout the app:
    //   t('key', 'Fallback string')
    //   t('key', { defaultValue: 'Fallback {{name}}', name: 'x' })
    let fallback
    if (typeof params === 'string') {
      fallback = params
      params = {}
    } else if (params && typeof params === 'object' && 'defaultValue' in params) {
      const { defaultValue, ...rest } = params
      fallback = defaultValue
      params = rest
    }

    const currentTranslations = translations[language] || translations[DEFAULT_LANGUAGE]
    let value = getValue(currentTranslations, key)

    // ★ PROD-690 / plan decision 5 (2026-10-07): a new key whose Spanish waits for Dr. Rami
    //   reads its approved stand-in under es (src/i18n/pendingEs.js), never the English below.
    if (value === undefined && language === 'es') {
      value = approvedText(translations, 'es', key)
    }

    // Fallback to English if key not found in current language
    if (value === undefined && language !== DEFAULT_LANGUAGE) {
      value = getValue(translations[DEFAULT_LANGUAGE], key)
    }

    // Use the inline fallback if provided; otherwise return the key (and log).
    if (value === undefined) {
      if (fallback !== undefined) {
        value = fallback
      } else {
        console.warn(`Missing translation: ${key}`)
        return key
      }
    }

    // Handle parameter interpolation (e.g., "Hello, {{name}}")
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue)
      })
    }

    return value
  }, [language])

  /**
   * ★ PROD-691 (P4-3): true when the UI language has APPROVED text for `key`: its own locale
   * file, or under es a PENDING_ES stand-in. A new sentence with none under es (Dr. Rami has
   * not approved it) is not drawn: the screen stays as it was, never English in a Spanish app.
   */
  const hasText = useCallback((key) => approvedText(translations, language, key) !== undefined, [language])

  /**
   * Set the current language
   * @param {string} langCode - Language code (e.g., 'es', 'zh')
   */
  const setLanguage = useCallback((langCode) => {
    // ★★ v242.172.0 — the OWNER enforces the enabled set now, not just the picker.
    if (isLanguageEnabled(langCode)) {
      setAppLanguage(langCode)
      setLanguageState(langCode)
    } else {
      console.warn(`Language ${langCode} is not offered in this build, falling back to ${DEFAULT_LANGUAGE}`)
      setAppLanguage(DEFAULT_LANGUAGE)
      setLanguageState(DEFAULT_LANGUAGE)
    }
  }, [])

  /**
   * Get current language info
   */
  const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0]

  /**
   * Check if current language is RTL
   */
  const isRTL = currentLanguage?.rtl || false

  /**
   * Translate user input to English for API/storage
   * This is a placeholder - in production, would use a translation API
   * For now, returns the original text (user types in their language, we store as-is)
   * The MD/Admin panels will receive English summaries generated by AI
   */
  const toEnglish = useCallback((text) => {
    // In production, this would call a translation API
    // For MVP, we'll store original text and let AI summarize in English
    return text
  }, [])

  /**
   * Translate English text to user's language
   * This is a placeholder - in production, would use a translation API
   */
  const fromEnglish = useCallback((text) => {
    // In production, this would call a translation API
    // For MVP, we'll display original text
    return text
  }, [])

  const value = {
    language,
    setLanguage,
    t,
    hasText,
    currentLanguage,
    isRTL,
    supportedLanguages: SUPPORTED_LANGUAGES,
    toEnglish,
    fromEnglish,
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
}

/**
 * Hook to use i18n functionality
 * @returns {object} - { language, setLanguage, t, currentLanguage, isRTL, supportedLanguages }
 */
export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}


export default I18nProvider
